import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { notification } from 'antd';

/**
 * Custom per-request flags layered on top of the Axios config. They are
 * stripped/honoured by the interceptors below:
 * - `__public` / `skipAuth` — suppress the session bearer (public endpoints
 *   whose credential is the token in the URL).
 * - `__skipAuthRefresh` — don't run the 401→refresh→retry machinery.
 * - `__skipAuthLogout` — a 401 on THIS request must NOT be treated as an
 *   expired session: don't refresh, don't hard-logout, don't clear the token,
 *   don't redirect to /login. Just reject the Promise so the caller handles it.
 *   Used by integration endpoints (e.g. Unipile LinkedIn) whose 401 means "the
 *   PLATFORM credential is invalid", not "the user's session expired" — logging
 *   the user out for a broken platform-side credential is the bug this guards.
 * - `__skipRetry` — don't retry on 5xx/408 (also skips the refresh redirect).
 * - `__skipErrorNotification` — don't fire the global red error toast (nor the
 *   5xx console.error) for this request. Used by getters that treat certain
 *   non-2xx responses as a normal "not configured yet" state — e.g. a 404 from
 *   `GET /document-identity` is by design (404 if unconfigured), so the page
 *   shows an empty form instead of an error popup.
 * - `__retryCount` — internal retry bookkeeping.
 */
interface JanusRequestFlags {
  __public?: boolean;
  skipAuth?: boolean;
  __skipAuthRefresh?: boolean;
  __skipAuthLogout?: boolean;
  __skipRetry?: boolean;
  __skipErrorNotification?: boolean;
  __retryCount?: number;
}
type JanusRequestConfig = AxiosRequestConfig & JanusRequestFlags;

const RETRY_STATUS_CODES = [500, 502, 503, 408];
const MAX_RETRIES = 3;
const BASE_DELAY = 1000;

// Cold start mitigation — Cloud Run services run with `min_instances=0` to save
// ~R$ 75k/year. First request of the day can take 5-15s while a container spins
// up; AI endpoints (LLM warmup + retrieval) can push that to 60-90s. The base
// timeout below covers a generic cold start; AI routes get a longer budget via
// the request interceptor a few lines down.
//
// Sized at 65s to stay above the BFF's 60s downstream timeout + 2s retry delay
// (see janus-bff COLD_START_DEFAULT_TIMEOUT_MS / COLD_START_RETRY_OPTIONS), so
// a single retry on a cold downstream service doesn't surface as a frontend
// ECONNABORTED.
const DEFAULT_TIMEOUT_MS = 65000;
// Must clear the BFF's AI budget (janus-bff env.ts aiRequestTimeoutMs = 130s) so a
// slow-but-succeeding grounded call (news-monitor, deep research) isn't aborted
// client-side before the BFF responds. Chain: FE (140s) > BFF (130s) > machine-ai (~120s).
const AI_TIMEOUT_MS = 140000;
const AI_URL_PATTERNS = [
  '/agent',
  '/chat',
  '/briefing',
  '/personalize',
  '/ai-search',
  '/conversation-intelligence',
  '/research', // news-monitor & co. are grounded LLM calls (slow) — need the AI budget
  '/inbox', // janus-messaging has min_instances=0; cold start can take 60-90s
];

const api = axios.create({
  baseURL: '/api/v1',
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

api.interceptors.request.use(
  (config) => {
    // Public/unauthenticated calls (e.g. the proposal accept link, whose
    // credential is the token in the URL path) must NOT carry the session
    // bearer — leaking the logged-in user's JWT to a public endpoint is a
    // security issue. Callers opt in via `{ __public: true }` (alias
    // `{ skipAuth: true }`); we then strip any inherited Authorization
    // header and never attach the stored token.
    const cfg = config as InternalAxiosRequestConfig & JanusRequestFlags;
    const isPublic = Boolean(cfg.__public || cfg.skipAuth);
    if (isPublic) {
      if (config.headers) delete config.headers.Authorization;
    } else {
      // Don't overwrite an Authorization header the caller explicitly set —
      // registration flows (signup OTP/resend-otp/verify-otp) sign with a
      // dedicated session token via `{ headers: { Authorization: ... } }`
      // and would break otherwise if a stale `accessToken` is in
      // localStorage from a previous session.
      const callerAlreadySetAuth = Boolean(config.headers?.Authorization);
      if (!callerAlreadySetAuth) {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    config.headers['X-Correlation-ID'] = generateUUID();

    // Bump timeout for AI endpoints — these chain LLM calls + retrieval and
    // routinely exceed the default budget on a cold container.
    const url = config.url || '';
    const isAiCall = AI_URL_PATTERNS.some((pattern) => url.includes(pattern));
    if (isAiCall && (!config.timeout || config.timeout < AI_TIMEOUT_MS)) {
      config.timeout = AI_TIMEOUT_MS;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Token refresh state — shared across concurrent 401s
let isRefreshing = false;

/**
 * Cada requisicao que tomou 401 enquanto UMA renovacao ja estava em curso
 * fica estacionada aqui — e precisa de saida pelos DOIS lados.
 *
 * MEDIDO 2026-09-07: `refreshSubscribers` guardava so' o callback de
 * SUCESSO, e `onRefreshFailed()` apenas esvaziava o array. A promessa criada
 * no interceptor nao tinha caminho de rejeicao: renovacao que falha deixava
 * TODA requisicao concorrente **pendente para sempre**. Nao e' o mesmo que
 * falhar — quem espera com `await` nunca chega ao `catch`, e um `finally`
 * que desliga spinner nunca roda.
 *
 * Onde isso aparece: numa rota autenticada o `window.location.href` navega e
 * a aba morre junto com as promessas, entao o defeito ficava invisivel. Nas
 * rotas de `publicPaths` (aceitar convite, cadastro, redefinir senha) NAO ha
 * navegacao — ali a tela simplesmente congela com o spinner girando, sem
 * mensagem de erro nenhuma, porque o `catch` de quem chamou nunca executa.
 */
type Estacionada = { ok: (token: string) => void; falhou: (erro: unknown) => void };
let refreshSubscribers: Estacionada[] = [];

function subscribeTokenRefresh(ok: (token: string) => void, falhou: (erro: unknown) => void) {
  refreshSubscribers.push({ ok, falhou });
}

function onTokenRefreshed(token: string) {
  // A lista e' esvaziada ANTES de notificar: um callback que dispare outro
  // 401 (e volte a estacionar) nao pode ser notificado por esta rodada, nem
  // apagado pela linha seguinte.
  const fila = refreshSubscribers;
  refreshSubscribers = [];
  fila.forEach(({ ok }) => ok(token));
}

function onRefreshFailed(erro: unknown) {
  const fila = refreshSubscribers;
  refreshSubscribers = [];
  fila.forEach(({ falhou }) => falhou(erro));
}

api.interceptors.response.use(
  (response) => {
    const correlationId = response.headers?.['x-correlation-id'];
    if (correlationId) {
      (response as unknown as Record<string, unknown>).correlationId = correlationId;
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & {
      __retryCount?: number;
      __skipAuthRefresh?: boolean;
      __skipAuthLogout?: boolean;
      __skipRetry?: boolean;
      __skipErrorNotification?: boolean;
    };
    if (!config) return Promise.reject(error);

    // Surface timeouts (likely cold start) in the console so support/observability
    // can correlate with Cloud Run cold-start metrics. ECONNABORTED is what Axios
    // emits when its own `timeout` fires.
    if (error.code === 'ECONNABORTED') {
      const targetUrl = config.url || '(unknown)';
      // eslint-disable-next-line no-console
      console.warn(`[api] Cold start detectado em ${targetUrl}`, {
        method: config.method,
        timeout: config.timeout,
      });
    }

    const status = error.response?.status;
    const url = config.url || '';

    // Integration endpoints whose 401 means "the PLATFORM credential is
    // invalid" (e.g. an expired/misconfigured Unipile key), NOT "the user's
    // session expired". A 401 on any of these — or any request explicitly
    // marked `__skipAuthLogout` — must never log the user out. Belt-and-braces
    // whitelist so even a caller that forgot the flag is protected.
    const INTEGRATION_SKIP_LOGOUT_ROUTES = ['/crm/unipile/', '/crm/connections/linkedin'];
    const skipAuthLogout =
      Boolean(config.__skipAuthLogout) ||
      INTEGRATION_SKIP_LOGOUT_ROUTES.some((route) => url.includes(route));

    // Retry on 5xx / 408 — but NEVER retry POST/PUT/DELETE (non-idempotent mutations)
    const retryCount = config.__retryCount || 0;
    const method = (config.method || 'get').toLowerCase();
    const isSafeMethod = method === 'get' || method === 'head' || method === 'options';
    if (!config.__skipRetry && isSafeMethod && status && RETRY_STATUS_CODES.includes(status) && retryCount < MAX_RETRIES) {
      config.__retryCount = retryCount + 1;
      const delay = BASE_DELAY * Math.pow(2, retryCount);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(config);
    }

    // Handle 401 — attempt token refresh (skip for auth endpoints)
    const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/signup', '/auth/forgot-password', '/auth/reset-password'];
    const isAuthEndpoint = AUTH_ENDPOINTS.some(ep => url.includes(ep));
    if (status === 401 && !config.__skipAuthRefresh && !isAuthEndpoint && !skipAuthLogout) {
      if (isRefreshing) {
        // Another request is already refreshing — wait for it
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (newToken: string) => {
              config.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(config));
            },
            // Sem este segundo braco a promessa fica pendente para sempre
            // quando a renovacao falha — ver o comentario de
            // `refreshSubscribers`.
            (erro) => reject(erro),
          );
        });
      }

      isRefreshing = true;

      try {
        /**
         * O BFF responde `{ accessToken, tokenType, expiresIn }` em camelCase
         * (`janus-bff/src/routes/auth/auth.routes.ts` — `res.json({ accessToken:
         * result.accessToken, ... })`). Ate 2026-09 este bloco lia so'
         * `data.access_token`, em snake_case: SEMPRE `undefined`, o `throw`
         * abaixo disparava em 100% das renovacoes, caia no `catch` e derrubava
         * o usuario para o login.
         *
         * O efeito era invisivel porque o sintoma ("preciso logar de novo de vez
         * em quando") e' indistinguivel de uma sessao que expira normalmente —
         * enquanto as ~40 linhas de fila de refresh acima (`isRefreshing`,
         * `subscribeTokenRefresh`, `onTokenRefreshed`) eram codigo morto
         * disfarcado de codigo vivo.
         *
         * As duas grafias sao aceitas de proposito: e' o mesmo padrao que
         * `normaliseCatalog` e `accountsApi` ja usam nesta base para atravessar
         * agregadores que achatam chave, e protege contra a direcao inversa
         * (um BFF que volte a emitir snake_case).
         */
        const { data } = await api.post<{ accessToken?: string; access_token?: string }>(
          '/auth/refresh',
          {},
          { __skipAuthRefresh: true } as JanusRequestConfig,
        );

        const newToken = data?.accessToken ?? data?.access_token;
        if (!newToken) throw new Error('No token in refresh response');
        localStorage.setItem('accessToken', newToken);

        config.headers.Authorization = `Bearer ${newToken}`;

        onTokenRefreshed(newToken);
        isRefreshing = false;

        return api(config);
      } catch (erroDaRenovacao) {
        // Refresh failed — force logout
        onRefreshFailed(erroDaRenovacao);
        isRefreshing = false;

        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        const publicPaths = ['/accept-invite', '/signup', '/verify', '/setup-company', '/forgot-password', '/reset-password', '/request-access', '/onboarding'];
        const isPublicPath = publicPaths.some(p => window.location.pathname.startsWith(p));
        if (window.location.pathname !== '/login' && !isPublicPath) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    if (status === 401 && !skipAuthLogout) {
      // This was a refresh request itself or __skipAuthRefresh — just clear
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      const publicPaths = ['/accept-invite', '/signup', '/verify', '/setup-company', '/forgot-password', '/reset-password', '/request-access', '/onboarding'];
      const isPublicPath = publicPaths.some(p => window.location.pathname.startsWith(p));
      if (window.location.pathname !== '/login' && !isPublicPath) {
        window.location.href = '/login';
      }
    } else if (status === 401) {
      // Marked `__skipAuthLogout` (or an integration route): a 401 here is a
      // platform-credential problem, not an expired session. Do NOT clear the
      // token, refresh, redirect, or toast — just reject so the caller renders
      // its own inline error.
    } else {
      const isAuthEp = url.includes('/auth/');
      const isRegistrationEndpoint = url.includes('/auth/register/');
      const isServerError = status !== undefined && status >= 500;
      const skipNotification =
        config.__skipErrorNotification ||
        (isAuthEp && (status === 429 || status === 423)) ||
        (isRegistrationEndpoint && status !== undefined && status >= 400);

      const data = error.response?.data as
        | { message?: string; detail?: string | { msg?: string }[] }
        | undefined;
      // FastAPI serializes HTTPException.detail on `detail`; validation
      // errors arrive as a list of { loc, msg, type } objects. Node/Express
      // upstreams tend to use `message`. Fall back in both directions so the
      // user sees the backend reason instead of a generic "Erro inesperado".
      let backendMessage: string | undefined;
      const rawError = (data as { error?: { message?: string } | string })?.error;
      if (typeof rawError === 'object' && typeof rawError?.message === 'string') {
        backendMessage = rawError.message;
      } else if (typeof rawError === 'string') {
        backendMessage = rawError;
      } else if (typeof data?.detail === 'string') {
        backendMessage = data.detail;
      } else if (Array.isArray(data?.detail)) {
        backendMessage = data.detail
          .map((d) => d?.msg)
          .filter(Boolean)
          .join('; ') || undefined;
      }
      const message =
        backendMessage ||
        data?.message ||
        error.message ||
        'Erro inesperado';

      if (isServerError) {
        // 5xx are backend/infra failures — log to the console only, never
        // surface a toast to the user (they can't act on them). Honour the
        // per-request opt-out so a caller that treats a status as expected
        // (e.g. an unconfigured-yet 404) doesn't pollute the console either.
        if (!config.__skipErrorNotification) {
          // eslint-disable-next-line no-console
          console.error(`[api] ${status} ${(config.method || 'GET').toUpperCase()} ${url} — ${message}`);
        }
      } else if (!skipNotification) {
        notification.error({
          message: 'Erro',
          description: message,
          duration: 5,
        });
      }
    }

    return Promise.reject(error);
  },
);

export default api;
